// /* global triggerOmnitureInteractions */
import _ from 'lodash';
import * as d3 from 'd3';
import moment from 'moment';
import helper from '../../../helper';

/* eslint no-shadow: ["error", {"allow": ["getters"]}] */
const getters = {
  crDataFilteredNoMissingDataRows: (state, getters, rootState) => {
    // The filter hides rows based on the report options setting from reportOptionsGo
    // and hides row for columns deselected via the CRColumnSelectModal or CRRemoveModal
    // via compareColumns.
    const filtered = _(state.crDataRaw).filter(o =>
      (state.reportOptionsGo.categoryFilterId ?
        o.c === state.reportOptionsGo.categoryFilterId : true)
      && (state.reportOptionsGo.stratFilterId ?
        // include if stratId1 matches the stratFilterId
        o.s1 === state.reportOptionsGo.stratFilterId
        // or the response name matches the strat filter name.
        // (We have to match on the names since the response Id will be different that the stratFilterId.)
        || rootState.lku.Response[o.r].name ===
          rootState.lku.Stratification[state.reportOptionsGo.stratFilterId].name
        : true)
      && (state.GSGo.reportType === 'Locations' ? _(state.compareColumns).find({ id: o.l }) : true)
      && (state.GSGo.reportType === 'Demographics' ? _(state.compareColumns).find({ id: o.s2 }) : true)
      && (state.GSGo.reportType === 'Years' ? _(state.compareColumns).find({ id: o.y }) : true))
      .value();
    return filtered;
  },
  categoryIndicatorIdsNotInCRDataFilteredNoMissingDataRows: (state, getters, rootState) => {
    const filtered = getters.crDataFilteredNoMissingDataRows;

    // find the category and indicator id's that are not in the filtered data
    // based on the reportType and categoryFilterId
    let categoryIndicatorIdsNotInFiltered =
      _.differenceWith(
        state.GSGo.reportType === 'Demographics' ?
          _(state.crDataRaw).uniqBy(o => `${o.c},${o.q}`).map(o => _.pick(o, ['c', 'q', 'y'])).value()
          : _(rootState.lpgsLink).map(o => ({ c: o.c, q: o.q, y: o.mry })).value(),
        _(filtered).uniqBy(o => `${o.c},${o.q}`).value(),
        (arrVal, othVal) => arrVal.c === othVal.c && arrVal.q === othVal.q,
      );
    if (state.reportOptionsGo.categoryFilterId) {
      // remove categories that don't match the categoryFilterId
      categoryIndicatorIdsNotInFiltered = _(categoryIndicatorIdsNotInFiltered)
        .filter({ c: state.reportOptionsGo.categoryFilterId }).value();
    }
    return categoryIndicatorIdsNotInFiltered;
  },
  crDataFiltered: (state, getters) => {
    const filtered = getters.crDataFilteredNoMissingDataRows;

    const ExcludeCategoryIndictorsIdsNotInFiltered = state.reportOptionsGo.hideMissingDataRows;
    return _.concat(
      filtered,
      ExcludeCategoryIndictorsIdsNotInFiltered ? []
        : getters.categoryIndicatorIdsNotInCRDataFilteredNoMissingDataRows,
    );
  },
  categoriesRaw: (state, getters, rootState) => _(state.crDataRaw).map('c').uniq().map(c => rootState.lku.Category[c])
    .orderBy('sort', 'asc')
    .value(),
  categoriesFiltered: (state, getters, rootState) => _(getters.crDataFiltered).map('c').uniq().map(c => rootState.lku.Category[c])
    .orderBy('sort', 'asc')
    .value(),
  searchHitsForCategory: (state, getters, rootState) => (categoryId) => {
    if (state.searchString === '' || getters.crDataFiltered.length === 0) return 0;

    const re = new RegExp(state.searchString, 'ig');

    // unique indicators and dvt's in this category
    const indicatorDvts = _(getters.crDataFiltered)
      .filter({ c: categoryId })
      .uniqBy(o => `${o.q},${o.dvt}`)
      .map(o => _.pick(o, ['q', 'dvt']))
      .value();

    // unique indicators in this category
    // indicators are displayed once per category
    // data types are displayed once per indicator
    const indicators = _(indicatorDvts).map('q').uniq().map(q => rootState.lku.Question[q])
      .value();

    let indicatorHits = 0;
    let dvtHits = 0;

    _(indicators).each((indicator) => {
      indicatorHits += (indicator.name.match(re) || []).length;
      const dvts = _(indicatorDvts)
        .filter({ q: indicator.id }).map('dvt').uniq()
        .map(dvt => rootState.dataTypes[dvt])
        .value();
      _(dvts).each((dvt) => {
        if (dvt) {
          dvtHits += (dvt.dataRowLabel.match(re) || []).length;
        }
      });
    });
    return indicatorHits + dvtHits;
  },
  searchHits: (state, getters) => {
    let hits = 0;
    _(getters.categoriesFiltered).each((c) => {
      hits += getters.searchHitsForCategory(c.id);
    });
    return hits;
  },
  compareColumnsLkuType: (state) => {
    const { reportType } = state.GSGo;
    if (reportType === 'Locations') return 'Location';
    if (reportType === 'Demographics') return 'Stratification';
    if (reportType === 'Years') return 'Year';
    throw new Error(`Unexpected reportType: ${reportType}`);
  },
  compareColumnsAlias: (state) => {
    const { reportType } = state.GSGo;
    if (reportType === 'Locations') return 'l';
    if (reportType === 'Demographics') return 's2';
    if (reportType === 'Years') return 'y';
    throw new Error(`Unexpected reportType: ${reportType}`);
  },
  compareColumnsSortOrder: (state) => {
    const { reportType } = state.GSGo;
    if (reportType === 'Locations') return 'asc';
    if (reportType === 'Demographics') return 'asc';
    if (reportType === 'Years') return 'desc';
    throw new Error(`Unexpected reportType: ${reportType}`);
  },
  compareColumnsMaxCount: (state) => {
    const { reportType } = state.GSGo;
    if (reportType === 'Locations') return 5;
    if (reportType === 'Demographics') return 0;
    if (reportType === 'Years') return 5;
    throw new Error(`Unexpected reportType: ${reportType}`);
  },
  allCompareColumns: (state, getters, rootState) => {
    const { reportType } = state.GSGo;
    const { lku } = rootState;
    if (reportType === 'Locations') return _(lku.Location).orderBy('sort', 'asc').value();
    if (reportType === 'Demographics') {
      return _(state.crDataRaw).map('s2').uniq()
        .compact() // remove undefined values from indicators w/o data rows
        .map(s => lku.Stratification[s])
        .orderBy('sort', 'asc')
        .value();
    }
    if (reportType === 'Years') return _(lku.Year).orderBy('sort', 'desc').value();
    throw new Error(`Unknown reportType: ${reportType}`);
  },
  csvExport: (state, getters, rootState) => {
    // csv export contains the same data as crDataFiltered but assumes  reportOption.hideMissingDataRows is true (the default)
    const { lku } = rootState;
    const { reportType } = state.GSGo;
    const filteredAndSortedIds = _(_.concat(
      getters.crDataFilteredNoMissingDataRows,
      getters.categoryIndicatorIdsNotInCRDataFilteredNoMissingDataRows,
    ))
    // add sort columns, set to 0 if the corresponding column is missing (indicators w/o data rows won't have dvt, r, s1, etc.)
      .map(o => ({
        ...o,
        csort: lku.Category[o.c].sort,
        qsort: lku.Question[o.q].sort,
        dvtsort: o.dvt ? lku.DataValueType[o.dvt].sort : 0,
        rsort: o.r ? lku.Response[o.r].sort : 0,
        s1sort: o.s1 ? lku.Stratification[o.s1].sort : 0,
        colSort: o[getters.compareColumnsAlias] ? lku[getters.compareColumnsLkuType].sort : 0,
      }))
      .orderBy(
        ['csort', 'qsort', 'dvtsort', 'rsort', 's1sort', 'colsort'],
        ['asc', 'asc', 'asc', 'asc', 'asc', getters.compareColumnsSortOrder],
      );

    const dataForCsv = _(filteredAndSortedIds).map((o) => {
      let { locationId } = state.GSGo;
      if (reportType === 'Locations') locationId = o.l;
      return {
        Year: o.y ? lku.Year[o.y].name : '',
        LocationAbbr: locationId ? lku.Location[locationId].abbr : '',
        LocationDesc: locationId ? lku.Location[locationId].name : '',
        Question: lku.Question[o.q].name,
        Category: lku.Category[o.c].name,
        StratificationCategory1: o.s1 ? lku.StratificationCategory[lku.Stratification[o.s1].parent].name : '',
        Stratification1: o.s1 ? lku.Stratification[o.s1].name : '',
        StratificationCategory2: o.s2 ? lku.StratificationCategory[lku.Stratification[o.s2].parent].name : '',
        Stratification2: o.s2 ? lku.Stratification[o.s2].name : '',
        Response: o.r ? lku.Response[o.r].name : '',
        DataValueType: o.dvt ? lku.DataValueType[o.dvt].name : '',
        Data_Value: o.dv,
        Data_Value_Footnote_Symbol: o.fs,
        Data_Value_Footnote: o.fs ? _(rootState.footnotes).find({ fs: o.fs, type: 'Footnote' }).ft : '',
        Low_Confidence_Limit: o.lci,
        High_Confidence_Limit: o.hci,
        WeightedNumber: o.wn,
      };
    }).value();

    const footnotes = _(rootState.footnotes)
      .filter(f => (f.type === 'MeasureDefinitions')
      || (f.type === 'GlobalNote')
      || (f.type === 'RelatedLinks')
      || (f.type === 'SuggestedCitation'))
      .orderBy(['type', 'sort'])
      .map(d => ({
        FootnoteText: d.type === 'SuggestedCitation' ? `${d.ft} [accessed ${moment().format('MMM D, YYYY')}]` : d.ft,
        Url: d.url ? helper.convertRelativeUrlToWCMSUrl(d.url) : '',
        FootnoteType: d.type,
      }))
      .value();

    return d3.csvFormat(_.concat(dataForCsv, footnotes));
  },
  tableSummary: (state, getters, rootState) => (categoryId) => {
    const { reportType, locationId, stratCatId } = state.GSGo;
    const { lku } = rootState;
    const category = lku.Category[categoryId].name;
    const compareColumnsList = _(state.compareColumns).map('name').value().join(', ');
    const compareColumnsCount = state.compareColumns.length;
    const location = lku.Location[locationId].name;

    if (reportType === 'Locations') {
      return `This table contains data for all indicators in the category ‘${category}’ and contains column headers for the Indicator, Data Type, Response, Disability Status & Types, and for the ${compareColumnsCount} selected location(s): ${compareColumnsList}.`;
    }
    if (reportType === 'Demographics') {
      const stratCategory = `${lku.StratificationCategory[stratCatId].name}(s)`;
      return `This table contains data for all indicators in the category ‘${category}’ for ${location} and contains column headers for the Indicator, Data Type, Response, Disability Status & Types, and for the ${compareColumnsCount} selected ${stratCategory}: ${compareColumnsList}.`;
    }
    if (reportType === 'Years') {
      return `This table contains data for all indicators in the category ‘${category}’ for ${location} and contains column headers for the Indicator, Data Type, Response, Disability Status & Types, and for the ${compareColumnsCount} selected Year(s): ${compareColumnsList}.`;
    }
    throw new Error(`Unexpected reportType: ${reportType}`);
  },
};


export default getters;



// WEBPACK FOOTER //
// ./src/store/modules/CR/getters.js